import {
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  TabContext,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, Staff, StaffEditResource, StaffViewResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { Services } from "./Services/Services";
import { StaffPageHeader } from "./StaffPageHeader";
import { StaffTabs } from "./StaffTabs";
import { StaffProfile } from "./StaffProfile/StaffProfile";

const routeName = "staff";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

class OptionalParams {
  tab?: StaffTab | undefined = undefined;
}

export const staffPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Staff",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type StaffTab = "Profile" | "Services";

export const StaffPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Staff;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const [currentTab, setCurrentTab] = useState<StaffTab>(optionalParams.tab || "Profile");
  const [staff, setStaff] = useState<Staff>({} as Staff);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [staff] });
  const [viewResource, setViewResource] = useState<StaffViewResource>({});
  const [editResource, setEditResource] = useState<StaffEditResource>({} as StaffEditResource);
  const { validationErrors, setValidationErrors } = useValidationErrors();

  // keeps track of which tabs data are loaded
  const [visitedTabs, setVisitedTabs] = useState<StaffTab[]>([]);

  useEffect(() => {
    fetch(params.id);
    fetchViewResource(params.id);
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  // load data from each tab as it's selected
  useEffect(() => {
    if (!visitedTabs.includes(currentTab)) {
      setVisitedTabs(
        produce((draft) => {
          draft.push(currentTab);
        })
      );
    }
  }, [currentTab]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${staffPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.staff.get(id).then((response) => {
      setStaff(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.staff.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.staff.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const save = () => {
    setValidationErrors({});
    const id = staff.id;

    if (id > 0) {
      ApiService.staff
        .saveUpdate(staff)
        .then(() => {
          reroute(id, "view");
          fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          setValidationErrors(ex.data);
        });
    } else {
      ApiService.staff
        .saveCreate(staff)
        .then((response) => {
          const newId = response;
          reroute(newId, "view");
          fetch(newId);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          setValidationErrors(ex.data);
        });
    }
  };

  return (
    <TabContext value={currentTab}>
      <Page
        isForm
        header={
          <StaffPageHeader
            rootPermission={ROOT_PERMISSION}
            crudMode={params.crudMode}
            reroute={reroute}
            setCurrentTab={setCurrentTab}
            staff={staff}
            viewResource={viewResource}
            unsavedChanges={unsavedChanges}
          />
        }
        tabs={<StaffTabs viewResource={viewResource} setCurrentTab={setCurrentTab} />}
      >
        <Box hidden={currentTab !== "Profile"}>
          <StaffProfile
            rootPermission={ROOT_PERMISSION}
            initialLoad={visitedTabs.includes("Profile")}
            id={params.id}
            staff={staff}
            setStaff={setStaff}
            crudMode={params.crudMode}
            viewResource={viewResource}
            editResource={editResource}
            validationErrors={validationErrors}
            reroute={reroute}
            save={save}
            unsavedChanges={unsavedChanges}
          />
          ˝
        </Box>

        <Box hidden={currentTab !== "Services"} height="100%" overflow="auto">
          <Services
            initialLoad={visitedTabs.includes("Services")}
            staffId={params.id}
            onGetList={() => fetchViewResource(params.id)}
          />
        </Box>
      </Page>
    </TabContext>
  );
};
